import { useEffect, useRef, useState } from 'react';
import { Header, Spinner } from 'view/components';
import { useContextSelector as useCS } from 'use-context-selector';
import {
  GlobalConfigContext,
  TGlobalConfigContextState,
} from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { RolesEnum } from 'infrastructure/enums/RolesEnum';
import { useGetChatsList } from 'view/pages/messenger/hooks/useGetChatsList';
import { useGetChatInfo } from 'view/pages/messenger/hooks/useGetChatInfo';
import { useNavigate } from 'react-router';
import {
  ChatMessagesFooter,
  NoMessagesBlock,
  ChatsListHeader,
  ChatsListMainItem,
  ChatMessagesHeader,
} from 'view/pages/messenger/components';
import {
  MESSAGES_PER_PAGE,
  useGetMessagesForCurrentConversation,
} from 'view/pages/messenger/hooks/useGetMessagesForCurrentConversation';
import UnavailableIconImage from 'assets/images/UnavailableIcon.jpg';

/* import { useChatSocket } from 'view/pages/messenger/hooks/useChatSocked'; */
/* import { storageService } from 'infrastructure/services/StorageService'; */
import { TUpdate } from 'domain/types';
import { updateMessengerUrl } from 'view/pages/messenger/helpers/index';
import { useMediaQuery } from '@mui/material';
import { theme } from 'view/themes/theme';
import * as S from './styled';
// eslint-disable-next-line no-restricted-imports
import { useChatSocketContext } from '../../../../App';

const ChatsPage = () => {
  const navigate = useNavigate();
  const matches = useMediaQuery(theme.breakpoints.down('laptop'));

  const [isChatOpen, setIsChatOpen] = useState(false);
  /* const [token, setToken] = useState<string | undefined>(undefined); */
  const [unreadMessagesPayload, setUnreadMessagesPayload] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const prevIdRef = useRef<string | null>(null);

  const { role, id } = useCS(GlobalConfigContext, globalConfigSelectors.userData);

  const globalUpdate: TUpdate<TGlobalConfigContextState> | undefined = useCS(
    GlobalConfigContext,
    globalConfigSelectors.update
  );
  const messages: any = useCS(GlobalConfigContext, globalConfigSelectors.messages);
  const params = new URLSearchParams(window.location.search);
  const conversationId: any = params.get('conversationId');

  const { isLoading, data, getChatsList } = useGetChatsList();
  const { isLoading: isLoadingChatInfo, data: chatInfoData, getChatInfo } = useGetChatInfo();
  const {
    isLoading: isLoadingChatMessages,
    // data: chatMessages,
    getChatMessages,
  } = useGetMessagesForCurrentConversation();
  const { emitEvent, subscribeToEvents, unsubscribeToEvents, isSocketConnected } =
    useChatSocketContext();

  const markMessagesAsRead = (conversationId: string) => {
    emitEvent('active-room', { conversationId: conversationId || null });
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (conversationId) markMessagesAsRead(conversationId);
  }, [messages]); // срабатывает при каждом обновлении списка сообщений

  /* useEffect(() => {
    const fetchToken = async () => {
      const t = await storageService.getItem('accessToken');
      setToken(t);
    };
    fetchToken();
  }, []); */

  useEffect(() => {
    if (!isSocketConnected) return;

    subscribeToEvents('message', (data) => {
      getChatsList();
      if (globalUpdate && conversationId && conversationId === data?.conversationId) {
        globalUpdate({ messages: [...messages, data] });
      }
    });

    subscribeToEvents('user', (data) => {
      if (data?.type === 'unreads') setUnreadMessagesPayload(data?.payload);
      if (data?.type === 'invite') getChatsList();
    });

    subscribeToEvents('conversation', (conversationData) => {
      if (data?.data?.length && data?.data?.length > 0) {
        const isConversationExist = data?.data?.some((el) => {
          return el.id === conversationData?.conversationId;
        });

        if (isConversationExist) {
          getChatsList();
        }
        if (isConversationExist && conversationId === conversationData?.conversationId) {
          getChatInfo(conversationData?.conversationId);
          getChatMessages(conversationData?.conversationId, 1, MESSAGES_PER_PAGE, () => {
            return markMessagesAsRead(conversationData?.conversationId);
          });
        }
      }
    });

    // eslint-disable-next-line consistent-return
    return () => {
      if (id && window.location.pathname !== '/messenger/chats') {
        emitEvent('close-active-room', {
          userId: id,
        });
      }
      unsubscribeToEvents('message');
      unsubscribeToEvents('user');
    };
  }, [isSocketConnected, messages]);

  useEffect(() => {
    getChatsList();
    return () => {
      if (globalUpdate) globalUpdate({ messages: [] });
    };
  }, []);

  // 1. Редирект (без изменений)
  useEffect(() => {
    if (!conversationId && data?.data?.length > 0 && !prevIdRef.current) {
      updateMessengerUrl(data.data[0].id, navigate);
    }
  }, [conversationId, data?.data]);

  // 2. Получение данных, если ID изменился
  useEffect(() => {
    if (conversationId && prevIdRef.current !== conversationId) {
      prevIdRef.current = conversationId;
      getChatInfo(conversationId);
      getChatMessages(conversationId, 1, MESSAGES_PER_PAGE, () => {
        return markMessagesAsRead(conversationId);
      });
    }
  }, [conversationId]);

  const groupMessagesByDate = (messages) => {
    const grouped = {};

    messages.forEach((message) => {
      // Получаем локальную дату сообщения (уже с учетом часового пояса)
      const messageDate = new Date(message.createdAt);

      // Ключ для группировки - дата в локальном времени
      const dateKey = messageDate
        .toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
        .replace(/\//g, '-');

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }

      grouped[dateKey].push(message);
    });

    return grouped;
  };

  const formatDate = (dateString) => {
    const now = new Date();
    const messageDate = new Date(dateString);

    // Устанавливаем время на 00:00:00 для сравнения только дат
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const messageDay = new Date(messageDate);
    messageDay.setHours(0, 0, 0, 0);

    if (messageDay.getTime() === today.getTime()) {
      return 'Today';
    }
    if (messageDay.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    }

    return messageDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderMessages = (messages) => {
    const groupedMessages = groupMessagesByDate(messages);
    const result: any = [];

    Object.keys(groupedMessages).forEach((date) => {
      // Добавляем разделитель с датой
      result.push(
        <div
          key={`date-${new Date()}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            textAlign: 'center',
            margin: '16px 0',
          }}
        >
          <span
            style={{
              flex: '1',
              height: '1px',
              backgroundColor: '#D8DBE8',
            }}
          />
          <span
            style={{
              width: 'max-content',
              backgroundColor: 'transparent',
              fontWeight: '400',
              fontSize: '12px',
              color: '#A9A9A9',
            }}
          >
            {formatDate(groupedMessages[date][0].createdAt)}
          </span>
          <span
            style={{
              flex: '1',
              height: '1px',
              backgroundColor: '#D8DBE8',
            }}
          />
        </div>
      );

      // Добавляем сообщения для этой даты
      groupedMessages[date].forEach((message) => {
        const userRole =
          !message?.user?.profile?.accountType && role?.name === 'recruiter'
            ? 'jobSeeker'
            : message?.user?.profile?.accountType;
        const isSender = message?.user?.profile?.accountType === role?.name;
        const avatarLink = message?.user?.photo?.link;
        const userName = message?.user?.userName;

        result.push(
          <S.ChatMessagesMainMessageItem
            key={message?.id}
            className={`${userRole} ${isSender ? 'left' : 'right'}`}
          >
            {!isSender && (
              <img
                src={
                  chatInfoData?.status?.name !== 'Active' && chatInfoData?.status?.id === 4
                    ? UnavailableIconImage
                    : chatInfoData?.status?.name === 'Active' || chatInfoData?.status?.id === 4
                      ? avatarLink
                      : UnavailableIconImage
                }
                alt="avatar"
                style={{ width: '32px', height: '32px' }}
              />
            )}
            <S.ChatMessagesMainMessageItemContent
              className={`${userRole} ${isSender ? 'sender' : 'receiver'}`}
            >
              <div>
                {!isSender && (
                  <S.ChatMessagesMainMessageItemContentTitle
                    className={`${userRole} ${isSender ? 'sender' : 'receiver'}`}
                  >
                    {(chatInfoData?.status?.name === 'Active' || chatInfoData?.status?.id === 4) &&
                      userName}
                    {chatInfoData?.status?.name !== 'Active' &&
                      chatInfoData?.status?.id !== 4 &&
                      role?.name === RolesEnum.JOB_SEEKER &&
                      'Recruiter unavailable'}
                    {chatInfoData?.status?.name !== 'Active' &&
                      chatInfoData?.status?.id !== 4 &&
                      role?.name === RolesEnum.RECRUITER &&
                      'Candidate unavailable'}

                    {chatInfoData?.status?.name !== 'Active' &&
                      chatInfoData?.status?.id === 4 &&
                      role?.name === RolesEnum.JOB_SEEKER &&
                      isSender &&
                      'Recruiter unavailable'}
                    {chatInfoData?.status?.name !== 'Active' &&
                      chatInfoData?.status?.id === 4 &&
                      role?.name === RolesEnum.RECRUITER &&
                      isSender &&
                      'Candidate unavailable'}
                  </S.ChatMessagesMainMessageItemContentTitle>
                )}
                <S.ChatMessagesMainMessageItemContentText
                  className={`${userRole} ${isSender ? 'sender' : 'receiver'}`}
                >
                  {message?.text}
                </S.ChatMessagesMainMessageItemContentText>
              </div>

              <p>{`${String(new Date(message.createdAt).getHours()).padStart(2, '0')}:${String(new Date(message.createdAt).getMinutes()).padStart(2, '0')}`}</p>
            </S.ChatMessagesMainMessageItemContent>
          </S.ChatMessagesMainMessageItem>
        );
      });
    });

    return result;
  };

  return (
    <S.Wrapper>
      <Header />
      <S.MainContentWrapper>
        {!isChatOpen || !matches ? (
          <S.ChatsList>
            <ChatsListHeader total={data?.total || 0} />
            {isLoading ? (
              <Spinner
                variant="outlined"
                style={{
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  backgroundColor: 'rgba(217, 225, 255, 0.3)',
                  width: '100%',
                  height: '100%',
                  zIndex: 10,
                }}
              />
            ) : (
              <S.ChatsListMain>
                {!isLoading && data?.data?.length > 0
                  ? data?.data?.map((item) => {
                      return (
                        <ChatsListMainItem
                          item={item}
                          key={item.id}
                          conversationId={conversationId}
                          unreadMessagesPayload={unreadMessagesPayload}
                          setIsChatOpen={setIsChatOpen}
                        />
                      );
                    })
                  : null}
              </S.ChatsListMain>
            )}
          </S.ChatsList>
        ) : null}
        {isChatOpen || !matches ? (
          <S.ChatMessages>
            {isLoadingChatInfo || isLoadingChatMessages ? (
              <Spinner
                variant="outlined"
                style={{
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  backgroundColor: 'rgba(217, 225, 255, 0.3)',
                  width: '100%',
                  height: '100%',
                  zIndex: 10,
                }}
              />
            ) : (
              <>
                <ChatMessagesHeader chatInfoData={chatInfoData} setIsChatOpen={setIsChatOpen} />
                <S.ChatMessagesMain>
                  {!messages.length ? (
                    <NoMessagesBlock title="No messages yet" subTitle="" />
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        height: '100%',
                        flexDirection: 'column',
                        gap: '8px',
                        overflow: 'auto',
                        padding: matches ? '0 10px 0 0' : '0 32px 0 0',
                      }}
                    >
                      {renderMessages(messages)}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </S.ChatMessagesMain>
                <ChatMessagesFooter chatInfoData={chatInfoData} getChatsList={getChatsList} />
              </>
            )}
          </S.ChatMessages>
        ) : null}
      </S.MainContentWrapper>
    </S.Wrapper>
  );
};

export default ChatsPage;
